/* eslint-disable no-restricted-syntax */
import { useGetBreeds } from '@application/api';
import { SeoController } from '@application/components';
import {
  filterRegionOptions,
  formatApiPrice,
  formatLocation,
  slugify,
  useModal,
  useRouter,
} from '@application/utils';
import type { CountryCode } from '@gql/graphql';
import type { CardItemStudData } from '@presentation/components';
import { PLACEHOLDER_IMAGE_DOG } from '@presentation/constants';
import type { StudListPageFormValues } from '@presentation/pages';
import { StudListPage } from '@presentation/pages';
import { useTranslation } from '@presentation/utils';
import { FormikProvider, useFormik } from 'formik';
import hash from 'object-hash';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useGetStuds } from './api/useGetStuds';
import { useStudWhereBuilder } from './api/useStudWhereBuilder';
import { FORM_INITIAL_VALUES, PAGINATED_SIZE } from './constants';
import { useSearch } from './useSearch';

export const StudListPageController: FC = () => {
  const { routeLocationDescriptor, infoFromCurrentPath, translate, countryCode } = useRouter();
  const { translate: translateResource } = useTranslation({ keyPrefix: 'studList' });

  const { modal: filtersModal, showFromClick: filtersShow } = useModal();

  const { studs, studsLoading, studsCount, studsRefetch, studsFetchMore, studsFetchMoreLoading } =
    useGetStuds();

  const { breedOptionsBasenameKey } = useGetBreeds();

  const [size, setSize] = useState(PAGINATED_SIZE);

  const { setSearchFromValues, valuesFromSearch } = useSearch();
  const { studWhereBuilder } = useStudWhereBuilder();

  const form = useFormik<StudListPageFormValues>({
    enableReinitialize: true,
    initialValues: valuesFromSearch,
    onSubmit: (values) => {
      setSearchFromValues(values);
      studsRefetch({ where: studWhereBuilder(values) });
    },
  });

  const formValuesChanged = useMemo(() => {
    const hashInitialValues = hash(FORM_INITIAL_VALUES);
    const hashValues = hash(form.values);
    return hashValues !== hashInitialValues;
  }, [form.values]);

  const formattedStuds: CardItemStudData[] | undefined = useMemo(() => {
    if (!studs || !studs.length) return;

    return studs.map((stud) => ({
      id: stud.id,
      name: stud.name,
      breed: stud.dogInfos?.dogBreed?.translations[0].name,
      champion: stud.studInfos?.champion ? translate('glossary.championLabel') : undefined,
      image: stud.imgs[0]?.small ?? PLACEHOLDER_IMAGE_DOG,
      location: formatLocation(
        {
          country: stud?.owner?.address?.country,
          region: stud?.owner?.address?.region,
        },
        translate,
      ),
      hasPedigreeFciBadge: stud.dogInfos?.pedigree === 'FCI',
      hasPedigreeBadge: !(
        !stud.dogInfos?.pedigree ||
        stud.dogInfos?.pedigree === 'no_pedigree' ||
        stud.dogInfos?.pedigree === 'FCI'
      ),
      price: formatApiPrice({ price: stud.studInfos?.price }),
      link: routeLocationDescriptor('stud', {
        params: [
          slugify(stud.dogInfos?.dogBreed?.translations[0].name as string),
          stud.id.toString(),
        ],
      }),
    })) as CardItemStudData[];
  }, [studs, translate, routeLocationDescriptor]);

  const regionOptions = filterRegionOptions(
    translate,
    countryCode,
    infoFromCurrentPath.search.countries as CountryCode[],
  );

  return (
    <>
      <SeoController
        title={translateResource('seo.title')}
        description={translateResource('seo.description')}
      />
      <FormikProvider value={form}>
        <StudListPage
          items={formattedStuds}
          loading={studsLoading}
          canLoadMore={size < studsCount}
          loadMoreText={`${Math.min(studs?.length ?? 0, studsCount)} / ${studsCount}`}
          onLoadMoreClick={async () => {
            await studsFetchMore({
              variables: {
                skip: size,
              },
            });

            setSize(size + PAGINATED_SIZE);
          }}
          loadMoreLoading={studsFetchMoreLoading}
          formOptions={{
            breeds: breedOptionsBasenameKey,
            regions: regionOptions,
          }}
          filtersModal={{
            modal: filtersModal,
            onShowClick: filtersShow,
            loading: studsLoading,
            canReset: formValuesChanged,
            onResetClick: () => {
              setSearchFromValues(FORM_INITIAL_VALUES);
              studsRefetch({ where: studWhereBuilder(FORM_INITIAL_VALUES) });
            },
          }}
        />
      </FormikProvider>
    </>
  );
};
